import React, { useState, useContext, useEffect } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import Fab from '@material-ui/core/Fab';
import Tooltip from '@material-ui/core/Tooltip';
import VideoCallIcon from '@material-ui/icons/VideoCall';
import RecordPermissionModal from './RecordPermissionModal/RecordPermissionModal';
import RecordingOptions from './RecordingOptions/RecordingOptions';
import StopIcon from '@material-ui/icons/Stop';
import download from 'downloadjs';
import VideoStreamMerger from 'video-stream-merger';
import { ToolBarContext } from '../../../App';
import { axClient } from '../../../axiosClient';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import UnMuteAlertDialog from './UnMuteAlertDialog/unMuteAlertDialog';
import useMainSpeaker from '../../../hooks/useMainSpeaker/useMainSpeaker';
import useScreenShareParticipant from '../../../hooks/useScreenShareParticipant/useScreenShareParticipant';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fab: {
      margin: theme.spacing(1),
    },
  })
);

let localChunks = [];

export default function Recording() {
  const classes = useStyles();
  const [showRecordPermissionModal, setShowRecordPermissionModal] = useState(false);
  const { room, hasMainVideo, isRecording } = useVideoContext();

  //toggle variable for start/stop icon
  const [start, setStart] = useState(true);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder>(null);
  const { setSnackBar, setSnackBarText } = useContext(ToolBarContext);
  const [isOptionsOpened, setIsOptionsOpened] = useState<boolean>(false);
  const [unmuteAlert, setUnMuteAlert] = useState(false);
  const mainParticipant = useMainSpeaker();
  const screenShareParticipant = useScreenShareParticipant();
  var temporaryStream = new MediaStream() as any;
  let videoSourceForRecorder = document.createElement('video') as any;
  videoSourceForRecorder.srcObject = temporaryStream.remoteStream;

  const stopRecording = () => {
    mediaRecorder.stop();
  };

  const handleDataAvailable = event => {
    if (event.data.size > 0) {
      localChunks.push(event.data);
    }
  };

  const mergeStreams = () => {
    const mainParticipantTracks = Array.from(mainParticipant.tracks.values()) as any;
    const mainParticipantVideo = mainParticipantTracks.find(p => p.trackName === 'camera') as any;
    const mainParticipantScreen = mainParticipantTracks.find(p => p.trackName === 'screen') as any;
    let mergedVideoHeight = 0;
    let mergedVideoWidth = 0;
    if (mainParticipantScreen) {
      mergedVideoWidth = mainParticipantVideo.track.dimensions.width;
      mergedVideoHeight = mainParticipantVideo.track.dimensions.height;
    } else if (mainParticipantVideo) {
      mergedVideoWidth = mainParticipantVideo.track.dimensions.width;
      mergedVideoHeight = mainParticipantVideo.track.dimensions.height;
    }
    var merger = new VideoStreamMerger({
      width: mergedVideoWidth, // Width of the output video
      height: mergedVideoHeight, // Height of the output video
    });

    //For canvas Stream
    let canvas = document.getElementsByClassName('lower-canvas')[0] as any;
    let canvasStream = canvas.captureStream();
    let audioStreams = [];

    //For participants audio Stream
    const participants = Array.from(room.participants.values());
    participants.forEach(participant => {
      const publications = Array.from(participant.tracks.values());
      const participantAudio = publications.find(p => p.kind === 'audio') as any;
      if (participantAudio) audioStreams = [...audioStreams, participantAudio.track.mediaStreamTrack];
    });

    //For localParticipant audio Stream
    const localParticipant = Array.from(room.localParticipant.tracks.values());
    const localAudio = localParticipant.find(p => p.kind === 'audio') as any;
    if (localAudio) audioStreams = [...audioStreams, localAudio.track.mediaStreamTrack];

    const audioContext = new AudioContext();
    const sources = audioStreams.map(t => audioContext.createMediaStreamSource(new MediaStream([t])));
    const combinedAudiostream = audioContext.createMediaStreamDestination();
    sources.forEach(source => source.connect(combinedAudiostream));

    if (mainParticipantVideo || mainParticipantScreen) {
      if (mainParticipantScreen) combinedAudiostream.stream.addTrack(mainParticipantScreen.track.mediaStreamTrack);
      else combinedAudiostream.stream.addTrack(mainParticipantVideo.track.mediaStreamTrack);
      merger.addStream(combinedAudiostream.stream, {
        x: 0, // position of the topleft corner
        y: 0,
        mute: audioStreams.length == 0,
      });
    } else canvasStream.addTrack(combinedAudiostream.stream.getAudioTracks()[0]);

    merger.addStream(canvasStream, {
      x: 0, // position of the topleft corner
      y: 0,
      mute: mainParticipantVideo || mainParticipantScreen,
    });
    merger.start();
    return merger.result;
  };

  const switchStreams = () => {
    const newStream = mergeStreams();
    videoSourceForRecorder.srcObject.replaceVideoTrack(newStream.getVideoTracks()[0]);
    videoSourceForRecorder.srcObject.replaceAudioTrack(newStream.getAudioTracks()[0]);
  };

  useEffect(() => {
    if (isRecording) switchStreams();
  }, [hasMainVideo, mainParticipant, screenShareParticipant]);

  const startRecording = async () => {
    localChunks = [];
    const mediaRecorder = new MediaRecorder(videoSourceForRecorder.captureStream(), {
      mimeType: 'video/webm; codecs=vp9',
    });
    let stream = mergeStreams();
    videoSourceForRecorder.srcObject.replaceVideoTrack(stream.getVideoTracks()[0]);
    videoSourceForRecorder.srcObject.replaceAudioTrack(stream.getAudioTracks()[0]);
    setMediaRecorder(mediaRecorder);
    mediaRecorder.ondataavailable = handleDataAvailable;
    mediaRecorder.start(1000);
  };

  const changeRecordAction = () => {
    if (start) {
      var mainVideoContainer = document.getElementById('main-container');
      let video = mainVideoContainer.getElementsByTagName('video')[0] as any;
      if (video) setShowRecordPermissionModal(true);
      else setUnMuteAlert(true);
    } else {
      setShowRecordPermissionModal(true);
    }
  };

  const downloadVideo = () => {
    var blob = new Blob(localChunks, {
      type: 'video/webm codecs=opus',
    });
    download(blob, 'video_' + Date.now() + '.webm');
  };

  const saveToNotes = () => {
    axClient
      .post('/event', {
        event_type: 'recording_updated',
        event_data: { recording_url: 'https://www.psgdover.com/assets/images/logos.svg' },
      })
      .then(() => {
        setSnackBar(true);
        setSnackBarText('Recording added to Job');
      })
      .catch(err => {
        throw err;
      });
  };

  return (
    <>
      <Tooltip
        title={!start ? 'Stop Recording' : 'start Recording'}
        placement="top"
        color="primary"
        PopperProps={{ disablePortal: true }}
      >
        <Fab className={classes.fab} onClick={() => changeRecordAction()}>
          {start ? <VideoCallIcon /> : <StopIcon color="secondary" />}
        </Fab>
      </Tooltip>
      {showRecordPermissionModal ? (
        <RecordPermissionModal
          showModal={showRecordPermissionModal}
          setShowModal={setShowRecordPermissionModal}
          start={start}
          setStart={setStart}
          startRecording={startRecording}
          stopRecording={stopRecording}
          isOptionsOpened={isOptionsOpened}
          setIsOptionsOpened={setIsOptionsOpened}
        />
      ) : null}
      {isOptionsOpened ? (
        <RecordingOptions
          show={isOptionsOpened}
          setShow={setIsOptionsOpened}
          saveToNotes={saveToNotes}
          download={downloadVideo}
        ></RecordingOptions>
      ) : null}
      {unmuteAlert && <UnMuteAlertDialog unMuteAlert={unmuteAlert} setUnMuteAlert={setUnMuteAlert} />}
    </>
  );
}
